import { Box, Grid, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BiFilterAlt } from 'react-icons/bi';
import {
  ICandidate,
  IJob,
  IJobCandidateInteraction,
  amplitude,
  ICandidateRecentConversation,
  ICandidateSubmissionSearchFilterVM,
  ICandidateFilter,
} from 'wisestep-api';
import {
  WsPagination,
  ScrollableTabs,
  TabTextVariant,
  WsListingLoader,
  IFilterList,
  WsFilterTags,
  IUserDTO,
  WsDrawer,
  WsColors,
  WsButton,
} from 'wisestep-base';

import { amplitudeEventSimilarJobsMapAsLeadANdSubmitSave } from '../../../utils/AmplitudeEvents';
import { paginationSettingsStore } from '.././../../../..//utils/PaginationSettingStore';
import BulkEmail from '.././../../../../components/BulkEmail';
import BulkSmsDrawer from '.././../../../../components/BulkSms/BulkSmsDrawer';
import { sortOptions } from '.././../../../../components/filters/components/AdvanceFilterHelper';
import WsJobsCrmPipelineAdvanceFilters from '.././../../../../components/filters/jobsCrmPipeline';
import JobsCrmPipelineAdvanceFiltersFormProps from '.././../../../../components/filters/models/JobsCrmPipelineAdvanceFiltersFormProps';
import NoDataFoundCard from '.././../../../../components/noDataFoundCard';
import { useUserContext } from '.././../../../../context';
import CandidateBulkSelectionBar from '.././../../../../features/candidates/components/candidateBulkSelectionBar';
import MappingCandidateCard from '.././../../../../features/candidates/mappings/components/mappingCard';
import { getInteraction } from '.././../../../../features/candidates/utils/CandidateHelper';
import conversationWidgetStore from '.././../../../../features/conversationWidget/store/conversationWidgetStore';
import { ITrackCvPages } from '.././../../../../features/conversationWidget/utils/cvHelper';
import { pipelineEmptyValues } from '.././../../../../features/jobsCrm/components/helper/JobCrmEmptyValues';
import {
  generateJobCrmBackwardURL,
  pushPipelineFilters,
} from '.././../../../../features/jobsCrm/components/helper/JobsCrmHelper';
import { JobsCrmDefaultRequest } from '.././../../../../features/jobsCrm/components/requests/JobsCrmDefaultRequest';
import useListingCandidates from '.././../../../../features/submissions/hooks/useListingCandidates';
import ShareCandidateProfileEmail from '.././../../../../features/submissions/submissionListing/components/ShareCandidateProfileEmail';
import AdvancedFilters from '.././../../../../features/submissionsNew/components/filters/advanced';
import ApplyedFilter from '.././../../../../features/submissionsNew/components/filters/smart/ApplyedFilter';
import useSubmissionsNewContext from '.././../../../../features/submissionsNew/components/hooks/useSubmissionsNewContext';
import {
  defaultSubmissionsListingSearchQuery,
  ISubmissionAdvanceFilters,
  getDefaultFilterValues,
  processUserInputsToFilters,
} from '.././../../../../features/submissionsNew/utils/Helper';
import ErrorLogger from '.././../../../../providers/ErrorLogger';
import { cacheJob } from '.././../../../../utils/cacheService';
import { useConversationWidgetStore } from '.././../../../../utils/useConversationWidgetStore';

interface CandidateListingProps {
  index?: number;
  similarJobs?: IJob[];
  job: IJob;
}

const tabList = (mappingCount?: number, submissionCount?: number) => {
  return [
    {
      tabName: 'Submission' + `(${submissionCount ?? 0})`,
    },
    {
      tabName: 'Mapping' + `(${mappingCount ?? 0})`,
    },
  ];
};
const CandidateListing = ({ index, similarJobs, job }: CandidateListingProps) => {
  const paginationData = paginationSettingsStore((state) => state.getPaginationSettings);
  const paginationSettings = paginationData();
  const [openBulkSms, setOpenBulkSms] = useState<boolean>(false);
  const [openEmail, setOpenEmail] = useState<boolean>(false);
  const defaultRequest = { ...JobsCrmDefaultRequest };
  const [cvIndex, setCvIndex] = useState<number | null>(null);
  const openModal = useConversationWidgetStore((state) => state.setCandidate);
  const updateView = useConversationWidgetStore((state) => state.updateView);
  const updateSelectedJob = conversationWidgetStore((state) => state.updateSelectedJob);
  const [tabIndex, setTabIndex] = useState<number>(0);
  defaultRequest.candidateSearchType = 'OnlyMappings';
  defaultRequest.candidateSearchTypeList = ['mapped'];
  defaultRequest.jobIds = similarJobs?.map((job) => job?.id ?? '').join(',') ?? '';
  const jobIdFilter: ICandidateFilter = {
    filterName: 'JOB_ID',
    values: similarJobs?.map((job) => job?.id ?? '') ?? [],
  };
  const [type, setType] = useState('submissions');
  const [query, setQuery] = useState<IJobCandidateInteraction>(defaultRequest);
  const [loader, setLoader] = useState<boolean>(false);
  const [advancedFilterDefaultValues, setAdvancedFilterFields] =
    useState<JobsCrmPipelineAdvanceFiltersFormProps>({});
  const [appliedFilters, setAppliedFilters] = useState<IFilterList[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [numberOfSelectedCandidates, setNumberOfSelectedCandidates] = useState<number>(0);
  const pageChange = (event, value) => {
    if (type === 'submissions') {
      const updatedQuery = { ...searchSubmissionQuery };
      updatedQuery.page = value;
      setSubmissionQuery(updatedQuery);
      setPageNumber(value + 1);
    } else {
      const updatedQuery = { ...query };
      updatedQuery.page = value;
      setQuery(updatedQuery);
      setPageNumber(value + 1);
    }
  };
  const [searchSubmissionQuery, setSubmissionQuery] = useState<ICandidateSubmissionSearchFilterVM>({
    ...defaultSubmissionsListingSearchQuery,
    filters: [jobIdFilter],
    size: Number(paginationSettings?.jobCrmPagination ?? 20) ?? 20,
  });
  const [openAdvancedFilters, setOpenAdvancedFilters] = useState<boolean>(false);

  const [advancedFilterDefaults, setAdvancedFilterDefaults] = useState<ISubmissionAdvanceFilters>(
    getDefaultFilterValues(searchSubmissionQuery)
  );
  const setIsWidgetOpen = useConversationWidgetStore((state) => state.setIsWidgetOpen);
  const navigationState = useConversationWidgetStore((state) => state.navigationState);
  const updateNavigationState = useConversationWidgetStore((state) => state.updateNavigationState);
  const navigate = useConversationWidgetStore((state) => state.navigate);
  const setCvViewSourcePage = useConversationWidgetStore((state) => state.setCvViewSourcePage);
  const data = useListingCandidates(query, setLoader, 'jobCRM');
  const { submissions } = useSubmissionsNewContext(searchSubmissionQuery, true);
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const [openShareCandidateEmail, setOpenShareCandidateEmail] = useState<boolean>(false);
  const [selectedCandidateEntitys, setSelectedCandidateEntitys] = useState<
    ICandidateRecentConversation[]
  >([]);
  const [candidateToShare, setCandidateToShare] = useState<ICandidate | null>();
  const [candidateList, setCandidateList] = useState<(ICandidate & { isSelected: boolean })[]>([]);

  useEffect(() => {
    const submissionsList = submissions?.content?.map((candidate) => ({
      ...candidate,
      isSelected: false,
    })) as (ICandidate & { isSelected: boolean })[];
    setCandidateList(submissionsList);
  }, [submissions]);

  useEffect(() => {
    const jobIdFilter: ICandidateFilter = {
      filterName: 'JOB_ID',
      values: similarJobs?.map((job) => job?.id ?? '') ?? [],
    };
    setSubmissionQuery({
      ...defaultSubmissionsListingSearchQuery,
      filters: [jobIdFilter],
      size: Number(paginationSettings?.jobCrmPagination ?? 20) ?? 20,
    });
    defaultRequest.jobIds = similarJobs?.map((job) => job?.id ?? '').join(',') ?? '';
    setQuery(defaultRequest);
  }, [similarJobs]);

  useEffect(() => {
    if (navigationState && navigate && cvIndex !== null) {
      if (cvIndex > 0 && cvIndex <= data.list.length - 1 && navigate == 'prev') {
        const index = cvIndex - 1;
        type === 'submissions' ? updateView('submission') : updateView('mapping');
        setCvIndex(index);
        openModal(data.list[index]);
      } else if (cvIndex >= 0 && cvIndex < data.list.length - 1 && navigate == 'next') {
        const index = cvIndex + 1;
        type === 'submissions' ? updateView('submission') : updateView('mapping');
        setCvIndex(index);
        openModal(data.list[index]);
      } else {
        setIsWidgetOpen(false);
      }
      updateNavigationState(false);
    }
  }, [
    cvIndex,
    data.list,
    navigate,
    navigationState,
    openModal,
    setIsWidgetOpen,
    type,
    updateNavigationState,
    updateView,
  ]);

  useEffect(() => {
    switch (index) {
      case 0:
        setCvViewSourcePage(ITrackCvPages.JOBCRM_SUBMISSION);
        break;
      case 1:
        setCvViewSourcePage(ITrackCvPages.JOBCRM_MAPPINGS);
        break;
    }
    sessionStorage.setItem('jobCrmUrl', jobCrmUrl);
  }, [index, setCvViewSourcePage]);

  const updateAdvancedQuery = (data: JobsCrmPipelineAdvanceFiltersFormProps) => {
    const updatedQuery = { ...query };
    [
      'emailAvailability',
      'phoneAvailability',
      'commPreferenceEmail',
      'commPreferencePhone',
    ].forEach((field) => delete updatedQuery[field]);
    updatedQuery.page = 0;
    updatedQuery.radiusInMiles = undefined;
    updatedQuery.sources = data.source ?? [];
    updatedQuery.tags = data.tags ?? [];
    updatedQuery.workAuthorizationCategories = [];
    data.workAuthorization?.forEach((obj) => {
      updatedQuery.workAuthorizationCategories?.push(obj);
    });
    updatedQuery.minSalary = data.salaryRange?.min ?? 0;
    updatedQuery.maxSalary = data.salaryRange?.max ?? 0;
    updatedQuery.salaryCurrency = data.salaryRange?.currency ?? '';
    updatedQuery.salaryTimeSpan = data.salaryRange?.unitTime ?? null;
    updatedQuery.noticePeriodMaxDays = data.noticePeriod?.max ?? 0;
    updatedQuery.noticePeriodMinDays = data.noticePeriod?.min ?? 0;
    updatedQuery.relocationPreference = data.relocationPreferences ?? null;
    if (data.currentStageCurrentStatusPairs) {
      const stageStstusObj = JSON.parse(data.currentStageCurrentStatusPairs);
      updatedQuery.currentStageCurrentStatusPairs = stageStstusObj.stageStatusData;
      updatedQuery.candidateJobStatus =
        stageStstusObj.jobOption === 'open_jobs'
          ? 'Opened'
          : stageStstusObj.jobOption === 'closed_jobs'
          ? 'Closed'
          : 'Any';
    } else {
      updatedQuery.currentStageCurrentStatusPairs = [];
      updatedQuery.candidateJobStatus = 'Any';
    }

    data.commPreferences?.forEach((value) => {
      if (value == 'Email') updatedQuery.commPreferenceEmail = true;
      else if (value == 'Phone') updatedQuery.commPreferencePhone = true;
    });
    data.contactAvailability?.forEach((value) => {
      if (value == 'Email') updatedQuery.emailAvailability = true;
      else if (value == 'Phone') updatedQuery.phoneAvailability = true;
      else if (value == 'Both') {
        updatedQuery.phoneAvailability = true;
        updatedQuery.emailAvailability = true;
      }
    });
    updatedQuery.createdByRecruiter = data.createdBy ?? [];
    return updatedQuery;
  };

  const onCVClick = (index: number) => {
    if (job) {
      cacheJob(job);
      updateSelectedJob(job);
    }
    type === 'submissions' ? updateView('submission') : updateView('mapping');
    setCvIndex(index);
  };

  const onDeleteFilterTag = (tag: string) => {
    setAppliedFilters((prev) => {
      const idOfChip = prev.find((existChip) => existChip.tag === tag)?.id ?? '';
      const advancedFilterDefaults: Partial<JobsCrmPipelineAdvanceFiltersFormProps> =
        advancedFilterDefaultValues;
      advancedFilterDefaults[idOfChip] = pipelineEmptyValues[idOfChip];
      if (tag.match('Business Definition')) {
        advancedFilterDefaults['currentStageCurrentStatusPairs'] =
          pipelineEmptyValues['currentStageCurrentStatusPairs'];
        advancedFilterDefaults['candidateJobStatus'] = pipelineEmptyValues['candidateJobStatus'];
      }
      setAdvancedFilterFields((prev) => ({ ...prev, ...advancedFilterDefaults }));
      setQuery(updateAdvancedQuery(advancedFilterDefaults));
      const index = prev.findIndex((existChip) => existChip.id === idOfChip);
      prev.splice(index, 1);
      return [...prev];
    });
  };
  const onApplyFilters = (data: JobsCrmPipelineAdvanceFiltersFormProps) => {
    setAdvancedFilterFields(data);
    setPageNumber(1);
    setQuery(updateAdvancedQuery(data));
    setAppliedFilters(pushPipelineFilters(data as JobsCrmPipelineAdvanceFiltersFormProps));
  };

  const jobCrmUrl = `/jobs-crm/${job.id}/false/0/similarJobs`;
  const ChangeIndex = {
    [0]: () => {
      setTabIndex(0);
      unselectAllCandidates();
      pageChange(null, 0);
      setCvIndex(null);
      window.history.replaceState(null, '', jobCrmUrl);
      setCvViewSourcePage(ITrackCvPages.JOBCRM_SUBMISSION);
      const updatedQuery = { ...defaultSubmissionsListingSearchQuery };
      updatedQuery.size = Number(paginationSettings?.jobCrmPagination ?? 20) ?? 20;
      updatedQuery.filters = [jobIdFilter];
      if (updatedQuery.filters) {
        updatedQuery.filters = [jobIdFilter];
      } else {
        updatedQuery.filters = [jobIdFilter];
      }

      setSubmissionQuery({ ...updatedQuery });
      setType('submissions');
    },
    [1]: () => {
      setTabIndex(1);
      unselectAllCandidates();
      pageChange(null, 0);
      setCvIndex(null);
      window.history.replaceState(null, '', jobCrmUrl);
      const updatedQuery = { ...query };
      updatedQuery.candidateSearchType = 'OnlyMappings';
      updatedQuery.candidateSearchTypeList = ['mapped'];
      setCvViewSourcePage(ITrackCvPages.JOBCRM_MAPPINGS);
      setQuery(updatedQuery);
      setType('mappings');
    },
  };

  const onSort = (event: SelectChangeEvent) => {
    query.sort = [event.target.value ?? 'createdOn,desc'];
    setQuery({ ...query });
  };
  const addSelectedCandidate = (candidate: ICandidate & { isSelected: boolean }) => {
    if (type === 'submissions') {
      const candidateIndex = candidateList.findIndex((c) => c.id === candidate.id);
      if (candidateIndex === -1) return;
      const updatedCandidatesList = [...candidateList];
      updatedCandidatesList[candidateIndex] = {
        ...candidateList[candidateIndex],
        isSelected: !candidateList[candidateIndex].isSelected,
      };
      setNumberOfSelectedCandidates((prevCount) =>
        updatedCandidatesList[candidateIndex].isSelected ? prevCount + 1 : prevCount - 1
      );
      setCandidateList(updatedCandidatesList);
    } else {
      setNumberOfSelectedCandidates((prevCount) =>
        candidate.isSelected ? prevCount - 1 : prevCount + 1
      );
      candidate.isSelected = !candidate.isSelected;
    }
  };

  const selectAllCandidates = () => {
    if (type === 'submissions') {
      selectCandidates(candidateList);
    } else {
      selectCandidates(data.list);
    }
  };

  const selectCandidates = (candidates) => {
    const selectedCandidates = candidates.filter(
      (candidate) => candidate.email && !candidate.isInDnc
    );
    selectedCandidates.forEach((candidate) => (candidate.isSelected = true));
    setNumberOfSelectedCandidates(selectedCandidates.length);
  };

  const unselectCandidates = (candidates) => {
    candidates.forEach((candidate) => {
      candidate.isSelected = false;
    });
    setNumberOfSelectedCandidates(0);
  };

  const unselectAllCandidates = () => {
    if (type === 'submissions') {
      unselectCandidates(candidateList);
    } else {
      unselectCandidates(data.list);
    }
  };

  const onShareEmailClick = () => {
    setCandidateToShare(null);
    setOpenShareCandidateEmail(true);
    const selectedEntitys = candidateList
      .map((candidate, index) => ({ isSelected: candidate.isSelected, index: index }))
      .filter((candidate) => candidate.isSelected)
      .map((item) => data.stageStatus[item.index]);

    setSelectedCandidateEntitys(selectedEntitys);
  };

  const onDeleteSubmissionFilterTag = (data) => {
    setOpenAdvancedFilters(false);

    const queryUpdate =
      data.split(' : ')[0] === 'SubmittedOrUpdatedbetween'
        ? 'SUBMITTED_OR_UPDATED_BETWEEN'
        : data.split(' : ')[0];
    const keyToRemove = data.split(' : ')[0] === 'SALARY' ? 'salaryRange' : data.split(' : ')[0];

    const updatedFilters = Object.keys(advancedFilterDefaults).reduce((result, key) => {
      if (key === keyToRemove) {
        if (keyToRemove === 'salaryRange') {
          result[key] = null;
        } else if (keyToRemove === 'SubmittedOrUpdatedbetween') {
          result[key] = '';
          result['SubmittedOrUpdatedFromDate'] = '';
          result['SubmittedOrUpdatedToDate'] = '';
        } else {
          result[key] = advancedFilterDefaults[key];
        }
      }

      return result;
    }, {} as ISubmissionAdvanceFilters);
    setAdvancedFilterDefaults(updatedFilters);
    const updatingFilters = searchSubmissionQuery.filters.filter(
      (filters) => filters.filterName !== queryUpdate
    );

    setSubmissionQuery({ ...searchSubmissionQuery, filters: updatingFilters });
  };

  const onFormSubmit = (data: ISubmissionAdvanceFilters) => {
    setOpenAdvancedFilters(false);
    const processedFilters = isEmpty(data) ? [] : processUserInputsToFilters(data);
    const updatedQuery = { ...searchSubmissionQuery, filters: processedFilters, page: 0 };
    if (updatedQuery.filters) {
      updatedQuery.filters = [...processedFilters, jobIdFilter];
    } else {
      updatedQuery.filters = [...processedFilters, jobIdFilter];
    }
    if (isEmpty(data)) {
      setSubmissionQuery({ ...defaultSubmissionsListingSearchQuery });
      setAdvancedFilterDefaults({} as ISubmissionAdvanceFilters);
    } else {
      setAdvancedFilterDefaults(data);
    }
    setSubmissionQuery(updatedQuery);
  };

  return (
    <Box>
      <Grid container xs={12}>
        <Grid item xs={6}>
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            <Box>
              <ScrollableTabs
                defaultValue={tabIndex}
                scrollList={tabList(data?.totalElements, submissions?.totalElements)}
                getTabIndex={(index) => ChangeIndex[index]()}
                variant={TabTextVariant.SUB_TITLE}
              />
            </Box>
          </ErrorBoundary>
        </Grid>
        <Grid
          item
          container
          xs={6}
          justifyContent="flex-end"
          spacing={1}
          display="flex"
          pb={1}
          pt={1}
        >
          <Grid item>
            <Select
              sx={{ width: 150 }}
              labelId="select-label-id"
              id="select-label"
              label="Sort by"
              onChange={onSort}
              defaultValue={'_score,desc'}
              size="small"
            >
              {sortOptions.map((option) => {
                return (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                );
              })}
            </Select>
          </Grid>
          <Grid item>
            <WsButton
              className="advance-filter"
              variant="outlined"
              size="medium"
              onClick={() => setOpenAdvancedFilters(!openAdvancedFilters)}
              startIcon={<BiFilterAlt />}
            >
              Advanced filters
            </WsButton>
          </Grid>
        </Grid>
      </Grid>
      <Box>
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          {type === 'submissions' ? (
            openAdvancedFilters && (
              <Grid
                container
                item
                xs={12}
                px={2}
                bgcolor={WsColors.White}
                pb={1}
                sx={{ border: '1px solid #E0E0E0', borderTop: 'none' }}
              >
                <ApplyedFilter
                  searchQuery={searchSubmissionQuery ?? []}
                  onDelete={(data) => onDeleteSubmissionFilterTag(data)}
                  getUpdatedFiltersFromAppliedFilters={(query) => setSubmissionQuery(query)}
                />
              </Grid>
            )
          ) : (
            <WsFilterTags filtersList={appliedFilters} onDelete={onDeleteFilterTag} />
          )}
        </ErrorBoundary>
      </Box>
      {openAdvancedFilters &&
        (type === 'submissions' ? (
          <WsDrawer
            title="Advanced Filters"
            open={openAdvancedFilters}
            onClose={() => setOpenAdvancedFilters(false)}
            size="small"
            headerSize="small"
          >
            <AdvancedFilters
              onClose={() => setOpenAdvancedFilters(false)}
              defaultValues={advancedFilterDefaults}
              onSubmit={onFormSubmit}
            />
          </WsDrawer>
        ) : (
          <WsJobsCrmPipelineAdvanceFilters
            open={openAdvancedFilters}
            onApplyFilters={(data: JobsCrmPipelineAdvanceFiltersFormProps) => {
              onApplyFilters && onApplyFilters(data);
              amplitude('JobCRM_Pipeline_advance_apply_CTA', {
                userId: loggedInUser.email,
                clickedOn: 'Advance Filter Apply',
                pageName: 'JobCrm',
                pageUrl: window.location.href,
              });
            }}
            filterClose={() => setOpenAdvancedFilters(false)}
            defaultValues={advancedFilterDefaultValues}
          />
        ))}
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        <Box mt={1}>
          <WsPagination
            count={Math.ceil(type === 'submissions' ? submissions?.totalPages : data.pagesCount)}
            onChange={pageChange}
            itemsPerPage={Number(paginationSettings.jobCrmPagination)}
            pageNumber={type === 'submissions' ? submissions?.number : query.page}
            defaultPage={pageNumber}
            isPageCount={true}
            type="Candidates"
            totalCount={type === 'submissions' ? submissions?.totalElements : data.totalElements}
          />
        </Box>
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        {((type === 'mappings' && data.list.length > 0) ||
          (type === 'submissions' && candidateList?.length > 0)) &&
        !loader ? (
          <Box>
            <Box>
              {type === 'submissions' ? (
                candidateList?.map((candidate, index) => (
                  <Box my={2} key={index}>
                    <MappingCandidateCard
                      candidate={candidate}
                      downloadSource="JOBCRM_MAPPINGS"
                      isSelected={candidate.isSelected}
                      openSingleEmail={() => {}}
                      onCVClick={onCVClick}
                      candidateIndex={index}
                      job={job}
                      checkBoxOnclick={() => addSelectedCandidate(candidate)}
                      interaction={getInteraction(data.stageStatus, candidate.email)}
                      amplitudeEventHandler={(eventType: string) => {
                        amplitudeEventSimilarJobsMapAsLeadANdSubmitSave[eventType](loggedInUser);
                      }}
                      array={candidateList.map((i) => i.candidateId ?? '')}
                      isSimijobs={true}
                      backwardpathArray={generateJobCrmBackwardURL(job.title)}
                    />
                  </Box>
                ))
              ) : (
                <>
                  {data.list?.map((candidate, index) => (
                    <Box my={2} key={index}>
                      {type === 'mappings' && (
                        <MappingCandidateCard
                          candidate={candidate}
                          downloadSource="JOBCRM_MAPPINGS"
                          isSelected={candidate.isSelected}
                          openSingleEmail={() => {}}
                          onCVClick={onCVClick}
                          candidateIndex={index}
                          job={job}
                          checkBoxOnclick={() => addSelectedCandidate(candidate)}
                          interaction={getInteraction(data.stageStatus, candidate.email)}
                          amplitudeEventHandler={(eventType: string) => {
                            amplitudeEventSimilarJobsMapAsLeadANdSubmitSave[eventType](
                              loggedInUser
                            );
                          }}
                          array={data.list.map((i) => i.candidateId ?? '')}
                          isSimijobs={true}
                          backwardpathArray={generateJobCrmBackwardURL(job.title)}
                        />
                      )}
                    </Box>
                  ))}
                </>
              )}
            </Box>
            <Grid item xs={12} container justifyContent="flex-end" my={2}>
              <WsPagination
                count={Math.ceil(
                  type === 'submissions' ? submissions?.totalPages : data.pagesCount
                )}
                onChange={pageChange}
                itemsPerPage={Number(paginationSettings.jobCrmPagination)}
                pageNumber={type === 'submissions' ? submissions?.number : query.page}
                defaultPage={pageNumber}
                isPageCount={false}
                type="Candidates"
                totalCount={
                  type === 'submissions' ? submissions?.totalElements : data.totalElements
                }
              />
            </Grid>
          </Box>
        ) : loader ? (
          <WsListingLoader />
        ) : (
          <NoDataFoundCard text="No Candidates Found" />
        )}
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        {openShareCandidateEmail && (
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            <ShareCandidateProfileEmail
              selectedCandidateEntitys={selectedCandidateEntitys}
              candidatesList={
                candidateToShare != null
                  ? [candidateToShare]
                  : data.list.filter(
                      (candidate) =>
                        candidate.isSelected &&
                        !candidate.isInDnc &&
                        (candidate.downloadSource = 'BULK_SUBMISSION')
                    )
              }
              open={openShareCandidateEmail}
              handleClose={() => {
                setOpenShareCandidateEmail(false);
                unselectAllCandidates();
              }}
            />
          </ErrorBoundary>
        )}
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        {numberOfSelectedCandidates > 0 && (
          <CandidateBulkSelectionBar
            selectedCandidates={numberOfSelectedCandidates}
            selectAllCandidates={selectAllCandidates}
            unselectAllCandidates={unselectAllCandidates}
            openBulkSms={() => setOpenBulkSms(true)}
            openEmail={() => setOpenEmail(true)}
            onShareEmailClick={type === 'submissions' ? onShareEmailClick : undefined}
          />
        )}
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        <BulkSmsDrawer
          open={openBulkSms}
          handleClose={() => setOpenBulkSms(false)}
          candidates={data.list.filter(
            (candidate) =>
              candidate.isSelected && !candidate.isInDnc && !isEmpty(candidate.phoneNumber)
          )}
          defaultJob={job}
        />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        {openEmail && (
          <BulkEmail
            open={openEmail}
            ignoreDuplicateCheck={true}
            handleClose={() => setOpenEmail(false)}
            candidates={data.list.filter(
              (candidate) =>
                candidate.isSelected &&
                !candidate.isInDnc &&
                (candidate.downloadSource = 'JOBCRM_MAPPINGS')
            )}
            drawer={true}
            defaultJob={job}
          />
        )}
      </ErrorBoundary>
    </Box>
  );
};
export default CandidateListing;
